import React, { useEffect, useMemo, useState } from 'react';
import { Form, Alert, Modal, Button } from 'react-bootstrap';
import { FaExclamationTriangle, FaTags,  } from 'react-icons/fa';
import { Option } from '../../../../../../../../types';
import {getAllItemHolderCategoriesByLocalId} from '../../../../../../../../../../../../../../crud/menu_api/categories';
import {getWorkstationsByLocalId} from '../../../../../../../../../../../../../../crud/management_api/workstations';
import SearchDropdown from '../../../../../../../../../../../../../../components/common/SearchDropdown/SearchDropdown';
import './CategorySelector.scss';

interface CategorySelectorProps {
  // Category (Most Important)
  menuCategories: Option[];
  selectedCategoryId: number | null | undefined;
  onCategorySelect: (id: number, name: string) => void;
  compactCategoryRow?: boolean;
  // Workstation override
  workstationId: number | null;
  setWorkstationId: (id: number | null) => void;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  menuCategories,
  selectedCategoryId,
  onCategorySelect,
  compactCategoryRow = false,
  workstationId,
  setWorkstationId
}) => {

  const isCategorySelected = Boolean(selectedCategoryId && !isNaN(selectedCategoryId) && selectedCategoryId > 0);

  // Local state to display routing phrase and modal
  const [showModal, setShowModal] = useState(false);
  const [categoryWsName, setCategoryWsName] = useState<string>('');
  const [workstationOptions, setWorkstationOptions] = useState<Option[]>([]);
  const [selectedWorkstationOption, setSelectedWorkstationOption] = useState<Option | null>(null);

  // Resolve effective target name
  const effectiveWorkstationName = useMemo(() => {
    if (workstationId != null) {
      const found = workstationOptions.find(w => w.id === workstationId);
      return found?.name || 'Puesto seleccionado';
    }
    return categoryWsName || 'puesto de la categoría';
  }, [workstationId, workstationOptions, categoryWsName]);

  // When category changes, fetch its default workstation info
  useEffect(() => {
    const userContext = localStorage.getItem('user');
    const localId = userContext != null ? JSON.parse(userContext).local_id : 0;
    if (isCategorySelected && localId) {
      getAllItemHolderCategoriesByLocalId(localId)
        .then((resp) => {
          const list = resp?.data || [];
          const found = list.find((c: any) => c.category_id === selectedCategoryId);
          if (found) {
            setCategoryWsName(found.workstation_name ?? '');
          } else {
            setCategoryWsName('');
          }
        })
        .catch(() => {
          setCategoryWsName('');
        });
    } else {
      setCategoryWsName('');
    }
  }, [selectedCategoryId, isCategorySelected]);

  // If we already have an override id but no name yet, try to resolve via fetching workstations
  useEffect(() => {
    if (workstationId != null && !workstationOptions.find(w => w.id === workstationId)) {
      const userContext = localStorage.getItem('user');
      const localId = userContext != null ? JSON.parse(userContext).local_id : 0;
      if (localId) {
        getWorkstationsByLocalId(String(localId)).then((resp) => {
          const data = resp?.data || [];
          const opts: Option[] = data.map((w: any) => ({ id: Number(w.workstation_id), name: w.workstation_name }));
          setWorkstationOptions(opts);
        }).catch(() => {});
      }
    }
  }, [workstationId, workstationOptions]);

  const openModal = () => {
    const userContext = localStorage.getItem('user');
    const localId = userContext != null ? JSON.parse(userContext).local_id : 0;
    if (localId) {
      getWorkstationsByLocalId(String(localId)).then((resp) => {
        const data = resp?.data || [];
        const opts: Option[] = data.map((w: any) => ({ id: Number(w.workstation_id), name: w.workstation_name, object: w }));
        setWorkstationOptions(opts);
        if (workstationId != null) {
          const sel = opts.find(o => o.id === workstationId) || null;
          setSelectedWorkstationOption(sel);
        } else {
          setSelectedWorkstationOption(null);
        }
        setShowModal(true);
      }).catch(() => setShowModal(true));
    } else {
      setShowModal(true);
    }
  };

  const handleSaveOverride = () => {
    if (selectedWorkstationOption) {
      setWorkstationId(selectedWorkstationOption.id);
    }
    setShowModal(false);
  };

  const handleClearOverride = () => {
    setWorkstationId(null);
    setShowModal(false);
  };

  // Compact single-row render for category only
  if (compactCategoryRow) {
    return (
      <div className="category-selector compact">
        <div className="compact-row">
          <Form.Label className="compact-label">Categoría</Form.Label>
          <div className="compact-selector">
            <SearchDropdown
              items={menuCategories}
              selected={menuCategories.find((c) => c.id === selectedCategoryId) || null}
              setSelected={(selected: Option) => {
                onCategorySelect(selected.id, selected.name);
              }}
              displayProperty="name"
              idProperty="id"
              placeholder="Seleccionar categoría..."
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="category-selector">
      {/* Category Selection - Priority Section */}
      <div className="priority-section">
        <div className="section-header">
          <FaTags className="section-icon" />
          <h6 className="section-title">Categoría del producto</h6>
        </div>
        
        {!isCategorySelected && (
          <Alert variant="warning" className="category-validation-alert">
            <div className="validation-content">
              <FaExclamationTriangle className="validation-icon" />
              <div className="validation-text">
                <strong>Categoría requerida:</strong> Selecciona el grupo donde se clasificará este producto
              </div>
            </div>
          </Alert>
        )}
        
        <div className="category-selector-wrapper">
          <SearchDropdown
            items={menuCategories}
            selected={menuCategories.find((c) => c.id === selectedCategoryId) || null}
            setSelected={(selected: Option) => {
              onCategorySelect(selected.id, selected.name);
            }}
            displayProperty="name"
            idProperty="id"
            placeholder="Buscar y seleccionar categoría..."
          />
        </div>

        {isCategorySelected && (
          <div className="mt-3" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-color)' }}>
              Las ordenes de este ítem serán enviadas a <strong>{effectiveWorkstationName}</strong>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={openModal}>Cambiar</Button>
              {workstationId != null && (
                <Button variant="outline-danger" size="sm" onClick={handleClearOverride}>Restablecer</Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Workstation selection modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header>
          <div className='form-modal-title-container'>
            <Modal.Title className='form-modal-title'>Seleccionar estación de trabajo</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div>
            <SearchDropdown
              items={workstationOptions}
              selected={selectedWorkstationOption}
              setSelected={(opt: Option) => setSelectedWorkstationOption(opt)}
              displayProperty="name"
              idProperty="id"
              placeholder="Buscar y seleccionar estación..."
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <Button className="form-button-cancel" variant="none" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button className="form-button-create" variant="none" onClick={handleSaveOverride} disabled={!selectedWorkstationOption}>
              Guardar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CategorySelector;
